import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { useQuery } from '@zg-rentals/react-query5';
import { useHpApiInquiryV3Handler_create } from '@zg-rentals/ts-hotpads-api-client-react-query';
import { getInquiryMessageCache } from '@/src/lib/api/endpoints/inquiry';
import type { InquiryMessageCache } from '@/src/lib/api/endpoints/inquiry';
import { inquiryKeys } from './queryKeys';
import { USER_LISTINGS_TAG } from './queryTags';

export const useInquiryMessageCache = (options?: { enabled?: boolean }) => {
  return useQuery<InquiryMessageCache | null>({
    queryKey: inquiryKeys.messageCache(),
    queryFn: async () => {
      const response = await getInquiryMessageCache();
      return response.data ?? null;
    },
    enabled: options?.enabled,
    staleTime: 0,
  });
};

/**
 * Submit a listing inquiry through v3.
 *
 * The inquirer email comes from the authenticated session, so the request body carries
 * none — which is why the caller must have a live session before this runs. Envelope
 * failures reject rather than resolving with a flag: branch on `v3ErrorReason`, not on
 * the response. Transport (CSRF header, envelope-error rejection) comes from the client
 * registered by configureHotpadsApiV3Client at bootstrap.
 */
export const useCreateInquiry = () => {
  return useHpApiInquiryV3Handler_create({
    // Metric names mirror legacy hotpads-web (ContactActions) so lead-submission
    // health is comparable across both apps in Marlin during the A/B test.
    onSuccess: () => {
      getGlobalMonitor()?.count({ name: 'ContactForm Submit Success' });
    },
    onError: () => {
      getGlobalMonitor()?.count({ name: 'ContactForm Submit Error' });
    },
    meta: {
      invalidates: [USER_LISTINGS_TAG, 'inquiry'],
      context: { api: 'hotpads-api', details: 'create listing inquiry v3' },
    },
  });
};
